// The caveats a customer should see before buying a lock, derived from the product's own
// data plus the few rules that apply to every digital lock (Hugh + Rob, 17 Sep 2026): the
// inside unit is never weather-rated, fingerprint readers want shelter, and so on. A
// product can add its own lines with `conditions: [...]`; nothing here is invented — every
// line points at a field on the product or a rule we apply on every install. CommonJS, no
// spread/destructuring (CRA content gotcha).
const fitLib = require('./lockFit');

function conditionsFor(product) {
  const out = [];
  const fit = product.fit || {};
  const digital = fitLib.isDigitalLock(product);
  const weather = fit.weatherRating || '';
  // One shared reading of the maker's rating (lockFit.weatherClass, also behind the chooser's
  // "Covered entry only" chip): "not IP-rated", "sheltered", "not published" all mean keep it
  // under cover; only a rating the maker actually states earns "fine on an exposed entry".
  const weatherClass = fitLib.weatherClass(product);
  const shelteredOnly = weatherClass === 'sheltered';
  const exposedOk = weatherClass === 'exposed';

  if (digital) {
    if (weather) out.push({ label: 'Outside unit', text: weather + (exposedOk ? ' — fine on an exposed entry.' : shelteredOnly ? '' : '.') });
    out.push({
      label: 'Inside unit',
      text: fit.sliding
        ? 'Not weather-rated, like every digital lock; on a sliding door it sits inside the room, out of the weather.'
        : 'Not weather-rated, like every digital lock. On an outward-opening door with no porch or eave the inside unit sits in the weather while the door is open; some makers require a weather cover for that. We check it before we book and bring one if the maker asks.',
    });
    if (fitLib.hasFingerprint(product) && shelteredOnly) {
      out.push({ label: 'Fingerprint reader', text: 'Sheltered doors only — the reader is not weather-rated. For an exposed door pick a keypad lock instead (E-LOK makes a keypad-only twin of each fingerprint model).' });
    }
    out.push({
      label: 'Other locks on the door',
      text: 'A digital lock replaces the main lock. Any extra mechanical lock on the door (a second deadbolt, a night latch) is disabled at install: we unlock it and take the inside turn off, so it stays on the door as trim but can never hold the door shut against a code. No holes to patch.',
    });
  }
  if (fit.doorThicknessMm && fit.doorThicknessMm.length === 2) {
    out.push({ label: 'Door thickness', text: fit.doorThicknessMm[0] + '–' + fit.doorThicknessMm[1] + ' mm. Thicker or thinner doors need an extension kit or a different lock — say so in the notes.' });
  }
  if (fit.backsetsMm && fit.backsetsMm.length) {
    const sizes = fit.backsetsMm.join(', ') + ' mm, measured from the door edge to the centre of the lock.';
    out.push({
      label: fit.sliding ? 'Sliding mortice' : 'Backset',
      text: fitLib.isBoreSwap(product)
        ? sizes + ' The latch adjusts between these; we set it on the day.'
        : sizes + ' Supplied cut to size; we confirm the size before it is ordered in.',
    });
  }
  if (fit.doorMaterials && fit.doorMaterials.length) {
    out.push({ label: 'Door types', text: fit.doorMaterials.map((m) => m.charAt(0).toUpperCase() + m.slice(1)).join(', ') + '.' });
  }
  const fire = (product.specs || []).find((row) => /fire/i.test(row[0]));
  if (fire) out.push({ label: 'Fire doors', text: fire[1] });
  for (const extra of product.conditions || []) out.push({ label: extra[0], text: extra[1] });
  return out;
}

// The same facts as a glance strip: four cells of two or three words each, for the top of
// the product page. The prose above stays available as the fine print. Cells with nothing
// to say are left out.
function glanceFor(product) {
  const fit = product.fit || {};
  const cells = [];
  const weather = fit.weatherRating || '';
  const ip = (weather.match(/IP\s?\d\d|IPX\d/i) || [])[0];
  const shelteredOnly = fitLib.weatherClass(product) === 'sheltered';
  const exposedOk = fitLib.weatherClass(product) === 'exposed';
  if (fitLib.isDigitalLock(product) && weather) {
    cells.push({
      label: 'Outside',
      value: ip ? ip.replace(/\s/g, '') : shelteredOnly ? 'Sheltered' : /mechanical/i.test(weather) ? 'Mechanical' : 'Weather-rated',
      sub: exposedOk ? 'Exposed entry OK' : shelteredOnly ? 'Keep it under cover' : 'Ask before an exposed door',
    });
  }
  if (fit.doorMaterials && fit.doorMaterials.length) {
    cells.push({
      label: 'Doors',
      value: fit.doorMaterials.map((m) => m.charAt(0).toUpperCase() + m.slice(1)).join(' · '),
      sub: fit.doorThicknessMm && fit.doorThicknessMm.length === 2 ? fit.doorThicknessMm[0] + '–' + fit.doorThicknessMm[1] + ' mm thick' : fit.sliding ? 'Sliding' : '',
    });
  }
  if (fit.backsetsMm && fit.backsetsMm.length) {
    const b = fit.backsetsMm;
    cells.push({
      label: fit.sliding ? 'Mortice' : 'Backset',
      value: b.length > 3 ? b[0] + '–' + b[b.length - 1] + ' mm' : b.join(' / ') + ' mm',
      sub: fitLib.isBoreSwap(product) ? 'Latch adjusts' : 'Cut to size',
    });
  }
  // the first period named in the warranty line (the operator's 12 months before the strike's 5 years)
  const period = (product.warrantyText || '').match(/(\d+)\s*-?\s*(years?|yrs?|months?)/i);
  if (product.warrantyText) {
    cells.push({
      label: 'Warranty',
      value: /lifetime/i.test(product.warrantyText)
        ? 'Lifetime' + (period ? ' / ' + period[1] + ' yrs electronics' : '')
        : period ? period[1] + ' ' + (/month/i.test(period[2]) ? 'months' : period[1] === '1' ? 'year' : 'years') : "Maker's",
      sub: product.install && product.install.eligible ? '+ 12 months on our install' : '',
    });
  }
  return cells;
}

// Short names for the makers' documents, so the page shows "User manual (PDF)" rather than
// the document's full title (kept as the link's tooltip).
function docKind(label) {
  const l = String(label || '');
  if (/consumer guarantee/i.test(l)) return 'Consumer guarantees';
  if (/warranty/i.test(l)) return 'Warranty terms';
  if (/care|maintenance/i.test(l)) return 'Care guide';
  if (/support page/i.test(l)) return 'Support page';
  if (/product page/i.test(l)) return 'Product page';
  if (/cut ?sheet|specification|specs/i.test(l)) return 'Specifications';
  if (/dimension/i.test(l)) return 'Dimensions';
  if (/template|drilling|tail bar|door preparation|routing|installation view/i.test(l)) return 'Fitting template';
  if (/installation|fitting instruction|install/i.test(l)) return 'Fitting guide';
  if (/user guide|user manual|manual|quick start|programming|code change|instructions/i.test(l)) return 'User manual';
  if (/brochure|flyer|catalogue/i.test(l)) return 'Brochure';
  return l;
}

module.exports = { conditionsFor, glanceFor, docKind };
